function humanizeType(type) {
  return String(type || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function toTitleCase(str) {
  return String(str || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const ALIASES = {
  ExceedsDevice: "ExceedsThreshold",
  NotConnected: "OfflineDevice",
  IsConnected: "OnlineDevice",
  FloodSuspected: "DetectsFlood",
  MotionDetectedWarning: "MotionDetected",
  MetTreshold: "ExceedsThreshold",
  FixedMetTreshold: "ReturnedToNormal",
};

const SEVERITY_META = {
  danger: {
    vuetifyType: "error",
    color: "red darken-1",
    bg: "rgba(229, 57, 53, 0.12)",
    icon: "mdi-alert-octagon",
  },
  warning: {
    vuetifyType: "warning",
    color: "orange darken-2",
    bg: "rgba(245, 124, 0, 0.12)",
    icon: "mdi-alert",
  },
  info: {
    vuetifyType: "info",
    color: "blue darken-1",
    bg: "rgba(25, 118, 210, 0.12)",
    icon: "mdi-information-outline",
  },
  success: {
    vuetifyType: "success",
    color: "green darken-1",
    bg: "rgba(67, 160, 71, 0.12)",
    icon: "mdi-check-circle-outline",
  },
  neutral: {
    vuetifyType: "info",
    color: "grey darken-1",
    bg: "rgba(120, 120, 120, 0.12)",
    icon: "mdi-bell-alert-outline",
  },
};

const KNOWN_TYPES = {
  ExceedsThreshold: {
    severity: "warning",
    icon: "mdi-thermometer-alert",
    label: { en: "Exceeds Sensor Threshold", he: "חריגה מסף חיישן" },
    description: {
      en: "Triggered when a sensor value goes above the defined limit.",
      he: "מופעל כאשר ערך החיישן עובר את הסף שהוגדר.",
    },
  },
  ReturnedToNormal: {
    severity: "info",
    icon: "mdi-check-circle-outline",
    label: { en: "Returned To Normal Condition", he: "חזרה למצב תקין" },
    description: {
      en: "Triggered when the sensor value returns to the normal range.",
      he: "מופעל כאשר ערך החיישן חוזר לטווח התקין.",
    },
  },
  DetectsFlood: {
    severity: "warning",
    icon: "mdi-water-alert",
    label: { en: "Detects Flood", he: "זיהוי הצפה" },
    description: {
      en: "Triggered when the sensor detects water or flooding.",
      he: "מופעל כאשר החיישן מזהה מים או הצפה.",
    },
  },
  FloodFixed: {
    severity: "info",
    icon: "mdi-water-check",
    label: { en: "Flood Is Over", he: "ההצפה הסתיימה" },
    description: {
      en: "Triggered when the flood condition is no longer detected.",
      he: "מופעל כאשר מצב ההצפה כבר לא מזוהה.",
    },
  },
  MagnetNotification: {
    severity: "warning",
    icon: "mdi-door",
    label: { en: "Magnet Notification", he: "התראת מגנט" },
    description: {
      en: "Triggered by magnet sensor state changes.",
      he: "מופעל בעקבות שינוי מצב של חיישן מגנט.",
    },
  },
  VibrationAlert: {
    severity: "warning",
    icon: "mdi-vibrate",
    label: { en: "Vibration Alert", he: "התראת רטט" },
    description: {
      en: "Triggered by vibration or movement state changes.",
      he: "מופעל בעקבות שינוי במצב רטט או תנועה.",
    },
  },
  MotionDetected: {
    severity: "warning",
    icon: "mdi-motion-sensor",
    label: { en: "Motion Detected", he: "זוהתה תנועה" },
    description: {
      en: "Triggered when motion is detected.",
      he: "מופעל כאשר מזוהה תנועה.",
    },
  },
  NoMotion: {
    severity: "info",
    icon: "mdi-motion-sensor-off",
    label: { en: "No Motion", he: "אין תנועה" },
    description: {
      en: "Triggered when no motion is detected for the configured period.",
      he: "מופעל כאשר לא מזוהה תנועה במשך הזמן שהוגדר.",
    },
  },
  ExceedsBetween: {
    severity: "warning",
    icon: "mdi-tune-variant",
    label: { en: "Exceeds Between", he: "חריגה/טווח מוגדר" },
    description: {
      en: "Triggered when a sensor value falls within or exceeds a defined range.",
      he: "מופעל כאשר ערך החיישן נכנס לטווח מוגדר או חורג ממנו.",
    },
  },
  ValueChanged: {
    severity: "info",
    icon: "mdi-swap-horizontal-bold",
    label: { en: "Value Changed", he: "שינוי ערך" },
    description: {
      en: "Triggered when a monitored value changes state.",
      he: "מופעל כאשר ערך מנוטר משנה מצב.",
    },
  },
  OfflineDevice: {
    severity: "danger",
    icon: "mdi-wifi-off",
    label: { en: "Disconnected", he: "התנתקות" },
    description: {
      en: "Connection to a device or sensor was lost.",
      he: "החיבור למכשיר או לחיישן נותק.",
    },
  },
  OnlineDevice: {
    severity: "success",
    icon: "mdi-wifi-check",
    label: { en: "Connected", he: "התחברות" },
    description: {
      en: "Connection to a device or sensor was restored.",
      he: "החיבור למכשיר או לחיישן חודש.",
    },
  },
  MalfunctionDevice: {
    severity: "danger",
    icon: "mdi-tools",
    label: { en: "Malfunction", he: "תקלה" },
    description: {
      en: "A device malfunction was detected.",
      he: "זוהתה תקלה במכשיר.",
    },
  },
  EmergencyButtonPressed: {
    severity: "danger",
    icon: "mdi-alert-octagon",
    label: { en: "Emergency Button Pressed", he: "לחצן חירום נלחץ" },
    description: {
      en: "Triggered when an emergency button is pressed.",
      he: "מופעל כאשר לחצן חירום נלחץ.",
    },
  },
};

function normalizeType(type) {
  const raw = String(type || "");
  return ALIASES[raw] || raw;
}

function normalizeSeverity(rawSeverity) {
  const val = String(rawSeverity || "").toLowerCase();
  if (["danger", "error", "critical", "red"].includes(val)) return "danger";
  if (["warning", "warn", "orange"].includes(val)) return "warning";
  if (["success", "positive", "ok", "green"].includes(val)) return "success";
  if (["info", "blue"].includes(val)) return "info";
  return "neutral";
}

function getUnknownDescription(locale, severity) {
  if (locale === "he") {
    if (severity === "warning" || severity === "danger") {
      return "התראה מסוג חדש הדורשת בדיקה במכשיר או בחיישן.";
    }
    if (severity === "info" || severity === "success") {
      return "עדכון מצב חדש ממכשיר או חיישן.";
    }
    return "התראת מערכת חדשה ממכשיר או חיישן.";
  }
  if (severity === "warning" || severity === "danger") {
    return "A new notification type that may require checking a device or sensor.";
  }
  if (severity === "info" || severity === "success") {
    return "A new status update from a device or sensor.";
  }
  return "A new system notification from a device or sensor.";
}

export function getNotificationMeta(input, options = {}) {
  const locale = options.locale === "he" ? "he" : "en";
  const rawType = typeof input === "string" ? input : input?.type;
  const type = normalizeType(rawType);

  const messageType =
    options.messageType ||
    (typeof input === "object" ? input?.data?.message?.type : null);
  const severity = normalizeSeverity(
    messageType || KNOWN_TYPES[type]?.severity
  );
  const severityMeta = SEVERITY_META[severity] || SEVERITY_META.neutral;
  const known = KNOWN_TYPES[type];

  const label = known
    ? known.label[locale]
    : toTitleCase(humanizeType(type || rawType || "Notification"));
  const description = known
    ? known.description[locale]
    : getUnknownDescription(locale, severity);

  return {
    type,
    rawType,
    severity,
    vuetifyType: severityMeta.vuetifyType,
    icon: known?.icon || severityMeta.icon,
    color: severityMeta.color,
    bg: severityMeta.bg,
    label,
    description,
  };
}

export function getAlertTypeMeta(type, options = {}) {
  return getNotificationMeta(type, options);
}
